import { responsiveFontSizes, ThemeProvider, createTheme } from "@mui/material";
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

export const ThemeModeContext = createContext({
  toggleMode: () => {},
  setLightMode: () => {},
  mode: "light",
  isMobile: "false",
  toggleMobile: () => {},
});

const THEME_STORAGE_KEY = "nepalex-theme-mode";

const getStoredMode = () => {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY);
  } catch {
    return null;
  }
};

const storeMode = (mode) => {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    // ignore write failures (e.g. private browsing storage restrictions)
  }
};

export const ThemeContextProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const stored = getStoredMode();
    if (stored === "light" || stored === "dark") {
      return stored === "dark";
    }
    return (
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    );
  });
  const [mode, setMode] = useState(isDarkMode ? "dark" : "light");
  const [width, setWidth] = useState(window.innerWidth);
  const [isMobile, setIsMobile] = useState(width <= 768);

  const handleWindowSizeChange = useCallback(() => {
    setWidth(window.innerWidth);
    setIsMobile(width <= 768);
  }, [width]);

  useEffect(() => {
    window.addEventListener("resize", handleWindowSizeChange);
    return () => {
      window.removeEventListener("resize", handleWindowSizeChange);
    };
  }, [handleWindowSizeChange]);

  const toggleMode = useCallback(() => {
    setMode((prevMode) => {
      const nextMode = prevMode === "light" ? "dark" : "light";
      storeMode(nextMode);
      return nextMode;
    });
    setIsDarkMode((prevMode) => !prevMode);
  }, []);

  const setLightMode = useCallback(() => {
    setMode("light");
    setIsDarkMode(false);
    storeMode("light");
  }, []);

  const colorMode = useMemo(
    () => ({
      toggleMode,
      setLightMode,
      mode,
      isDarkMode,
      isMobile,
    }),
    [mode, isDarkMode, isMobile, toggleMode, setLightMode]
  );

  let theme = createTheme({
    palette: {
      mode: isDarkMode ? "dark" : "light",
      primary: {
        main: "#FF9944",
        light: "#f0ca92",
        dark: "#cd7539",
      },
      secondary: {
        main: "#418fdd",
        light: "#75baf0",
        dark: "#336cb8",
      },
      background: {
        default: isDarkMode ? "#303030" : "#f8f8f8",
        paper: isDarkMode ? "#424242" : "#fff",
        imageCaption: isDarkMode ? "#c5c7cb" : "#616161",
      },
      text: {
        primary: isDarkMode ? "#fff" : "#000",
        secondary: isDarkMode ? "#bdbdbd" : "#616161",
        white: "#fff",
      },
      divider: isDarkMode ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)",
    },
  });

  theme = responsiveFontSizes(theme);

  return (
    <ThemeModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export default ThemeModeContext;
